<template>
  <div class="employment-templates text-dark">
    <div class="employment-templates__header">
      <h2>Stillingskategorier</h2>
      <button class="do-bttn do-bttn--submit" @click="openCreate">
        Opret stillingskategori
      </button>
    </div>

    <div v-if="isLoading">
      <Loader></Loader>
    </div>

    <table class="do-table do-table--striped" v-else>
      <thead>
      <tr>
        <th>Titel</th>
        <th>Vikarløn</th>
        <th>Std. Lønkode ved vikarløn</th>
        <th>Std. Omkostningssted ved vikarløn </th>
        <th>UV-grænse</th>
        <th>Lønkode</th>
        <th v-if="showSpecialSkoleSalaryCodeColumn">Spec-skole lønkode</th>
        <th style="text-align: right"></th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="templates.length === 0">
        <td :colspan="emptyColspan"><i>Ingen stillingskategorier oprettet</i></td>
      </tr>

      <tr v-for="tpl in templates" :key="tpl.id">
        <td>{{ tpl.title }}</td>

        <td>
          <span class="do-pill do-pill--small do-pill--yellow" v-if="tpl.manual_salary">Vikarløn</span>
          <span class="muted" v-else>—</span>
        </td>

        <!-- Std. lønkode: vises kun hvis manual_salary -->
        <td>
          <template v-if="tpl.manual_salary">
            <span v-if="salaryCodeTitle(tpl.salary_code_id)">{{ salaryCodeTitle(tpl.salary_code_id) }}</span>
            <i v-else class="muted">—</i>
          </template>
          <i v-else class="muted">—</i>
        </td>

        <!-- Std. omkostningssted: vises kun hvis manual_salary -->
        <td>
          <template v-if="tpl.omkostningssted">
            <span v-if="costCenterTitle(tpl.omkostningssted)">{{ costCenterTitle(tpl.omkostningssted) }}</span>
            <i v-else class="muted">—</i>
          </template>
          <i v-else class="muted">—</i>
        </td>

        <td>
          <template v-if="!tpl.manual_salary && tpl.hour_limit">
            {{ tpl.hour_limit }}
          </template>
          <i v-else class="muted">—</i>
        </td>

        <td>
          <template v-if="!tpl.manual_salary && tpl.hour_limit_salary_code_id">
            <span v-if="salaryCodeTitle(tpl.hour_limit_salary_code_id)">{{ salaryCodeTitle(tpl.hour_limit_salary_code_id) }}</span>
            <i v-else class="muted">—</i>
          </template>
          <i v-else class="muted">—</i>
        </td>

        <td v-if="showSpecialSkoleSalaryCodeColumn">
          <template v-if="!tpl.manual_salary && tpl.special_hour_limit_salary_code_id">
            <span v-if="salaryCodeTitle(tpl.special_hour_limit_salary_code_id)">{{ salaryCodeTitle(tpl.special_hour_limit_salary_code_id) }}</span>
            <i v-else class="muted">—</i>
          </template>
          <i v-else class="muted">—</i>
        </td>
        <td class="actions">
          <div class="icon__bg-circle" @click="openEdit(tpl)" title="Redigér">
            <span class="icon-pencil"></span>
          </div>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Create / Edit modal -->
    <DoEmploymentTemplate
      v-if="isTemplateModalOpen"
      :theEditEmploymentTemplate="editTemplate"
      @created="onTemplateCreated"
      @updated="onTemplateUpdated"
      @deleted="onTemplateDeleted"
      @close="closeTemplateModal"
    />
  </div>
</template>

<script>
import Loader from '../../common/Loader.vue'
import eventbus from '../../../eventbus'
import DoEmploymentTemplate from '../SalarySettings/DoEmploymentTemplate.vue'

export default {
  name: 'EmploymentTemplatesTable',
  components: { DoEmploymentTemplate, Loader },
  props: {
    selectedEmploymentIds: {
      type: Array,
      default: () => []
    },
    initiallyLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: this.initiallyLoading,
      isTemplateModalOpen: false,
      editTemplate: null,
      isSubmitting: false
    }
  },
  computed: {
    templates () {
      return this.$store.getters.employmentTemplatesSorted
    },
    showSpecialSkoleSalaryCodeColumn () {
      // Spec-skole uv-tillæg er midlertidigt skjult for SD.
      if (this.$store.getters.salarySystem === 'SD') return false
      return this.templates.some(template => template.special_hour_limit_salary_code_id != null && template.special_hour_limit_salary_code_id !== '')
    },
    emptyColspan () {
      return this.showSpecialSkoleSalaryCodeColumn ? 8 : 7
    },
    salaryCodesSorted () {
      return this.$store.getters.salaryCodesSorted || []
    },
    // map { id -> vTitle }
    salaryCodeMap () {
      const map = {}
      for (let i = 0; i < this.salaryCodesSorted.length; i++) {
        const sc = this.salaryCodesSorted[i]
        map[sc.id] = sc.vTitle
      }
      return map
    },
    // costCenters: forventet shape [{ code, title }]
    costCenters () {
      return this.$store.getters.costCentersAll || []
    },
    // map { codeString -> title }
    costCenterMap () {
      const m = {}
      for (let i = 0; i < this.costCenters.length; i++) {
        const c = this.costCenters[i]
        // normaliser til string-nøgle (da tpl.omkostningssted kan være number/null/0)
        const key = typeof c.code === 'number' ? String(c.code) : c.code
        m[key] = c.title
      }
      return m
    }
  },
  methods: {
    // ----- Formattere -----
    salaryCodeTitle (id) {
      if (id === null || typeof id === 'undefined' || id === '') return ''
      return this.salaryCodeMap[id] || ''
    },
    costCenterTitle (code) {
      if (code === null || typeof code === 'undefined' || code === '') return ''
      const key = typeof code === 'number' ? String(code) : code
      if (this.costCenterMap[key]) {
        return this.costCenterMap[key]
      }
      // Hvis ikke fundet i map, formater koden: store bogstaver, max 4 tegn
      const formatted = String(code).toUpperCase().substring(0, 4)
      return formatted
    },

    // ----- Data -----
    readAllEmployments () {
      this.isLoading = true
      // Hent skabeloner og lønkoder
      Promise.resolve()
        .then(() => this.$store.dispatch('readEmploymentTemplates'))
        .then(() => this.$store.dispatch('readSalaryCodes'))
        .then(() => {
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },

    // ----- UI -----
    openCreate () {
      this.editTemplate = null
      this.isTemplateModalOpen = true
    },
    openEdit (tpl) {
      this.editTemplate = tpl
      this.isTemplateModalOpen = true
    },
    closeTemplateModal () {
      this.editTemplate = null
      this.isTemplateModalOpen = false
    },

    // ----- Actions -----
    onSelect (tpl) {
      if (!this.selectedEmploymentIds || this.selectedEmploymentIds.length === 0) {
        eventbus.toast = { text: 'Vælg personale først', success: false }
        return
      }
      this.$emit('select', tpl)
    },
    onTemplateCreated () {
      this.closeTemplateModal()
      this.readAllEmployments()
    },
    onTemplateUpdated () {
      this.closeTemplateModal()
      this.readAllEmployments()
    },
    onTemplateDeleted () {
      this.closeTemplateModal()
      this.readAllEmployments()
    }
  },
  mounted () {
    this.readAllEmployments()
  }
}
</script>

<style scoped>
.employment-templates {
  padding: 1rem;
  max-width: 1440px;
}

.employment-templates__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.icon-col {
  text-align: center;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.do-table tr:nth-child(2n) {
  background-color: #f9fafb;
}

.d-tag {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.muted {
  color: #6b7280;
}
</style>



// WEBPACK FOOTER //
// EmploymentTemplates.vue?52db4c8b